import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Bell, FileText, Wrench, MessageSquare, TrendingUp, ArrowRight } from "lucide-react";

export function TodayWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/shop/today");
        setData(res.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="border border-white/10 bg-[#121214] p-6 mb-10 animate-pulse h-40" />;
  }
  if (!data) return null;

  const fresh = data.since_last_visit || 0;
  const matching = data.matching_jobs_24h || 0;
  const new24 = data.new_jobs_24h || 0;

  return (
    <section className="mb-10" data-testid="today-widget">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
        {/* Hero "since last visit" */}
        <div className="lg:col-span-4 bg-[#121214] p-6 sm:p-8 flex flex-col justify-between min-h-[180px]">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-[#FF3B30]" />
            <span className="label-eyebrow text-zinc-400">SINCE YOUR LAST VISIT</span>
          </div>
          {fresh > 0 ? (
            <>
              <div>
                <div className="font-heading text-5xl sm:text-6xl font-black tracking-tighter leading-none">
                  <span className="text-[#FF3B30]">{fresh}</span> <span className="text-zinc-600 text-2xl sm:text-3xl align-middle">new job{fresh !== 1 ? "s" : ""}</span>
                </div>
                {matching > 0 && (
                  <p className="text-sm text-zinc-400 mt-3">
                    <span className="text-white font-medium">{matching}</span> match your specialties in the last 24 hours.
                  </p>
                )}
              </div>
              <Link to="/browse" className="btn-primary mt-4 inline-flex items-center gap-2 w-fit" data-testid="today-browse-btn">
                Browse jobs <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : new24 > 0 ? (
            <>
              <div>
                <div className="font-heading text-5xl sm:text-6xl font-black tracking-tighter leading-none">
                  <span className="text-white">{new24}</span> <span className="text-zinc-600 text-2xl sm:text-3xl align-middle">posted today</span>
                </div>
                {matching > 0 && (
                  <p className="text-sm text-zinc-400 mt-3">
                    <span className="text-[#FF3B30] font-medium">{matching}</span> match your specialties.
                  </p>
                )}
              </div>
              <Link to="/browse" className="btn-secondary mt-4 inline-flex items-center gap-2 w-fit">
                Browse jobs <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <div>
                <div className="font-heading text-3xl font-bold tracking-tight">You're all caught up.</div>
                <p className="text-sm text-zinc-500 mt-2">No new jobs since you last looked. We'll email you when fresh work matching your specialties comes in.</p>
              </div>
            </>
          )}
        </div>

        {/* 4 metric tiles */}
        <Tile
          label="ACTIVE QUOTES"
          value={data.active_quotes}
          icon={FileText}
          tone="white"
          subtitle="Awaiting an owner's decision"
        />
        <Tile
          label="IN PROGRESS"
          value={data.in_progress_jobs}
          icon={Wrench}
          tone="orange"
          subtitle="Jobs you're currently doing"
          link="/shop"
        />
        <Tile
          label="UNREAD MESSAGES"
          value={data.unread_messages}
          icon={MessageSquare}
          tone={data.unread_messages > 0 ? "red" : "white"}
          subtitle="Reply to keep the deal moving"
          link="/messages"
          accent={data.unread_messages > 0}
        />
        <Tile
          label="COMPLETED · ALL TIME"
          value={data.completed_jobs}
          icon={TrendingUp}
          tone="green"
          subtitle="Lifetime jobs done"
        />
      </div>
    </section>
  );
}

function Tile({ label, value, subtitle, icon: Icon, tone = "white", link, accent }) {
  const toneColors = {
    red: "text-[#FF3B30]",
    orange: "text-[#FF9500]",
    green: "text-[#34C759]",
    blue: "text-[#007AFF]",
    white: "text-white",
  };
  const content = (
    <div className={`bg-[#121214] p-5 h-full flex flex-col justify-between min-h-[180px] ${link ? "hover:bg-[#1a1a1d] transition-colors cursor-pointer" : ""}`} data-testid={`today-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        <Icon className={`w-4 h-4 ${accent ? toneColors[tone] : "text-zinc-600"}`} strokeWidth={1.5} />
      </div>
      <div>
        <div className={`font-heading text-4xl font-black ${toneColors[tone]}`}>{value ?? 0}</div>
        <p className="text-[11px] text-zinc-500 mt-1.5 leading-snug">{subtitle}</p>
      </div>
    </div>
  );
  if (link) return <Link to={link} className="lg:col-span-2">{content}</Link>;
  return <div className="lg:col-span-2">{content}</div>;
}
